const CNPJ_BLACKLIST = new Set([
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999'
])

const LOOSE_STRIP_REGEX = /[^\dA-Z]/g
const STRICT_STRIP_REGEX = /[./-]/g
const STRICT_MASK_REGEX = /^(?:[\dA-Z]{2}\.[\dA-Z]{3}\.[\dA-Z]{3}\/[\dA-Z]{4}-\d{2}|[\dA-Z]{12}\d{2})$/

/**
 * Removes mask characters and normalizes CNPJ input.
 * Loose mode uppercases and strips everything except 0-9 and A-Z.
 */
export function stripCnpj (value: string, strict = false): string {
  if (typeof value !== 'string') return ''
  const normalized = value.toUpperCase()
  const regex = strict ? STRICT_STRIP_REGEX : LOOSE_STRIP_REGEX
  return normalized.replace(regex, '')
}

/**
 * Calculates a CNPJ check digit using modulo 11 with ASCII-48 conversion (RFB NT 49/2024).
 */
export function cnpjVerifierDigit (digits: string): number {
  let sum = 0
  let weight = 2

  for (let i = digits.length - 1; i >= 0; i--) {
    sum += (digits.charCodeAt(i) - 48) * weight
    weight = weight === 9 ? 2 : weight + 1
  }

  const mod = sum % 11
  return mod < 2 ? 0 : 11 - mod
}

/**
 * Validates numeric and alphanumeric CNPJ values (14 chars, numeric check digits).
 */
export function validateCNPJValue (value: string, strict = false): boolean {
  if (strict && (typeof value !== 'string' || !STRICT_MASK_REGEX.test(value.toUpperCase()))) {
    return false
  }

  const stripped = stripCnpj(value, strict)

  if (!stripped || stripped.length !== 14 || CNPJ_BLACKLIST.has(stripped)) {
    return false
  }

  if (!/^\d{2}$/.test(stripped.slice(-2))) {
    return false
  }

  let numbers = stripped.slice(0, 12)
  numbers += String(cnpjVerifierDigit(numbers))
  numbers += String(cnpjVerifierDigit(numbers))

  return numbers.slice(-2) === stripped.slice(-2)
}

/**
 * Applies the standard CNPJ mask (XX.XXX.XXX/XXXX-YY), including partial input while typing.
 */
export function formatCnpj (value: string): string {
  const stripped = stripCnpj(value).slice(0, 14)

  if (!stripped) return ''

  if (stripped.length === 14) {
    return stripped.replace(
      /^([\dA-Z]{2})([\dA-Z]{3})([\dA-Z]{3})([\dA-Z]{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    )
  }

  let masked = ''

  for (let i = 0; i < stripped.length; i++) {
    if (i === 2 || i === 5) masked += '.'
    if (i === 8) masked += '/'
    if (i === 12) masked += '-'
    masked += stripped[i]
  }

  return masked
}

/** @deprecated Prefer stripCnpj — kept as alias for backwards compatibility */
export const normalizeCnpj = stripCnpj
